/*
 * fcr-job-board frontend JavaScript.
 *
 * Progressive enhancement for the archive: filter form and pagination work
 * fine without JS. With JS, we intercept submit / chip change / keyword input /
 * pagination, then swap the #fcr-jb-archive region in place via fetch() — same
 * URL, same template, no new endpoint, no full reload.
 */
(function () {
	'use strict';

	const rootId = 'fcr-jb-archive';
	// Typing is slow + uncertain; chip clicks are deliberate but multi-select.
	const keywordDebounceMs = 350;
	const chipDebounceMs = 200;
	let debounceTimer = null;
	let controller = null;

	function getRoot() {
		return document.getElementById(rootId);
	}

	function getForm() {
		const root = getRoot();
		return root ? root.querySelector('.fcr-jb-filters') : null;
	}

	function buildUrlFromForm() {
		const form = getForm();
		if (!form) {
			return window.location.href;
		}
		const qs = new URLSearchParams(new FormData(form)).toString();
		return form.action + (qs ? '?' + qs : '');
	}

	function fetchAndSwap(url, pushState) {
		const root = getRoot();
		if (!root) {
			return;
		}

		// Cancel any in-flight request so a slow earlier fetch can't overwrite a fresher one.
		if (controller) {
			controller.abort();
		}
		controller = new AbortController();

		// Capture focus before the swap so we can restore it to the equivalent
		// element in the new DOM. Without this, toggling a chip via keyboard
		// (Tab → Space/Enter) loses focus when the form is replaced and the
		// next Tab restarts from the document top.
		const focusState = captureFocusState();

		fetch(url, {
			signal: controller.signal,
			credentials: 'same-origin',
			headers: { 'X-Requested-With': 'fcr-job-board' },
		})
			.then(function (response) {
				return response.text();
			})
			.then(function (html) {
				const doc = new DOMParser().parseFromString(html, 'text/html');
				const fresh = doc.getElementById(rootId);
				if (fresh) {
					root.replaceWith(fresh);
					restoreFocus(focusState);
				}
				if (pushState !== false) {
					window.history.pushState({ jb: true }, '', url);
				}
				// New count is announced via aria-live="polite" on the count line — no focus move.
			})
			.catch(function () {
				// Aborted or network/parse error — leave the existing region in place.
			});
	}

	// Build a stable selector for the currently focused element so we can
	// re-focus it after the swap. Returns null if focus is outside the form
	// region (e.g., user tabbed away during the debounce). For text-like
	// inputs, also captures the caret position — without it, focusing a
	// freshly-parsed input drops the caret to position 0 in some browsers,
	// which is jarring when the user pauses mid-word.
	function captureFocusState() {
		const root = getRoot();
		if (!root) {
			return null;
		}
		const el = root.ownerDocument.activeElement;
		if (!el || el === root.ownerDocument.body) {
			return null;
		}
		if (!root.contains(el)) {
			return null;
		}
		const selector = selectorFor(el);
		if (!selector) {
			return null;
		}
		const state = { selector };
		if (el.matches('input[type="search"], input[type="text"]')) {
			state.selectionStart = el.selectionStart;
			state.selectionEnd = el.selectionEnd;
		}
		return state;
	}

	function selectorFor(el) {
		if (el.id) {
			return '#' + CSS.escape(el.id);
		}
		if (el.matches('input[type="checkbox"]')) {
			const name = el.getAttribute('name');
			const value = el.getAttribute('value');
			if (name && value) {
				// CSS.escape guards against future term slugs containing chars
				// that have meaning in CSS selectors (", \, ], etc.). Current
				// slugs go through sanitize_title() so this is defensive.
				return 'input[name="' + CSS.escape(name) + '"][value="' + CSS.escape(value) + '"]';
			}
		}
		if (el.matches('.fcr-jb-filters__submit')) {
			return '.fcr-jb-filters__submit';
		}
		if (el.matches('.fcr-jb-filters__reset')) {
			return '.fcr-jb-filters__reset';
		}
		return null;
	}

	function restoreFocus(state) {
		if (!state) {
			return;
		}
		const el = document.querySelector(state.selector);
		if (!el) {
			return;
		}
		el.focus({ preventScroll: true });
		if (typeof state.selectionStart === 'number' && typeof el.setSelectionRange === 'function') {
			try {
				el.setSelectionRange(state.selectionStart, state.selectionEnd);
			} catch (e) {
				// setSelectionRange throws on inputs that don't support selection
				// (e.g., type=email in some engines). Safe to swallow — focus is set.
			}
		}
	}

	// Delegated handlers — bound once on document, survive root swaps.

	document.addEventListener('submit', function (e) {
		const form = e.target;
		if (!form.classList || !form.classList.contains('fcr-jb-filters')) {
			return;
		}
		if (!getRoot()) {
			return;
		}
		e.preventDefault();
		window.clearTimeout(debounceTimer);
		fetchAndSwap(buildUrlFromForm(), true);
	});

	document.addEventListener('input', function (e) {
		const input = e.target;
		if (!input.matches || !input.matches('.fcr-jb-filters input[type="search"]')) {
			return;
		}
		window.clearTimeout(debounceTimer);
		debounceTimer = window.setTimeout(function () {
			fetchAndSwap(buildUrlFromForm(), true);
		}, keywordDebounceMs);
	});

	document.addEventListener('change', function (e) {
		const input = e.target;
		if (!input.matches || !input.matches('.fcr-jb-filters input[type="checkbox"]')) {
			return;
		}
		window.clearTimeout(debounceTimer);
		debounceTimer = window.setTimeout(function () {
			fetchAndSwap(buildUrlFromForm(), true);
		}, chipDebounceMs);
	});

	// Chips look like buttons but are checkboxes underneath, so the default
	// Enter behavior is "submit the form" rather than "toggle the chip" —
	// surprising for users following the visual affordance. Intercept Enter
	// on a focused chip checkbox: toggle it manually and dispatch `change`
	// so the existing chip-debounce handler picks it up. Space still works
	// natively for users who know the standard checkbox key.
	document.addEventListener('keydown', function (e) {
		if (e.key !== 'Enter') {
			return;
		}
		const target = e.target;
		if (!target.matches || !target.matches('.fcr-jb-filters input[type="checkbox"]')) {
			return;
		}
		e.preventDefault();
		target.checked = !target.checked;
		target.dispatchEvent(new Event('change', { bubbles: true }));
	});

	document.addEventListener('click', function (e) {
		const root = getRoot();
		if (!root || !root.contains(e.target)) {
			return;
		}

		const reset = e.target.closest('.fcr-jb-filters__reset');
		if (reset) {
			e.preventDefault();
			const form = getForm();
			if (form) {
				// Clear synchronously so chips/keyword visually update before the fetch resolves.
				form.reset();
				form.querySelectorAll('input[type="checkbox"]').forEach(function (cb) {
					cb.checked = false;
				});
				const keyword = form.querySelector('input[type="search"]');
				if (keyword) {
					keyword.value = '';
				}
			}
			fetchAndSwap(reset.href, true);
			return;
		}

		const pageLink = e.target.closest('.fcr-jb-archive__results a.page-numbers');
		if (pageLink) {
			e.preventDefault();
			fetchAndSwap(pageLink.href, true);
			window.scrollTo({
				top: root.getBoundingClientRect().top + window.scrollY - 20,
				behavior: 'smooth',
			});
		}
	});

	window.addEventListener('popstate', function () {
		fetchAndSwap(window.location.href, false);
	});
})();
